import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getProjectById } from "~/store/selectors/selector.projects";
import { getLoggedUser } from "~/store/selectors/selector.users";
import { isClient, isFinancialManager, isMember } from "~/utils/userHelper";

export const useProjectProgressVisibility = (projectId) => {
  const loggedUser = useSelector(getLoggedUser);
  const project = useSelector((state) => getProjectById(state, projectId));

  const isMemberWithoutExtraPermissions =
    isMember(loggedUser) && project?.leader_id !== loggedUser?.id && !isFinancialManager(loggedUser);

  const showTrackedVsEstimatedProgressForRole = useMemo(() => {
    if (isMemberWithoutExtraPermissions) {
      return false;
    }
    if (isClient(loggedUser) && !project?.show_task_estimates_to_clients) {
      return false;
    }
    return true;
  }, [isMemberWithoutExtraPermissions, loggedUser, project?.show_task_estimates_to_clients]);

  const showTrackingProgressForRole = useMemo(() => {
    if (isMemberWithoutExtraPermissions) {
      return false;
    }
    if (isClient(loggedUser) && !project?.is_client_reporting_enabled) {
      return false;
    }
    return true;
  }, [isMemberWithoutExtraPermissions, loggedUser, project?.is_client_reporting_enabled]);

  const showBudgetVsCostProgressForRole = useMemo(() => {
    if (isMemberWithoutExtraPermissions) {
      return false;
    }
    if (isClient(loggedUser)) {
      return false;
    }
    return true;
  }, [isMemberWithoutExtraPermissions, loggedUser]);

  const showTrackedVsEstimatedProgress = useMemo(() => {
    return showTrackedVsEstimatedProgressForRole && project?.task_estimates_enabled;
  }, [project?.task_estimates_enabled, showTrackedVsEstimatedProgressForRole]);

  const showTrackingProgress = useMemo(() => {
    return showTrackingProgressForRole && project?.is_tracking_enabled;
  }, [project?.is_tracking_enabled, showTrackingProgressForRole]);

  const showBudgetVsCostProgress = useMemo(() => {
    return (
      showBudgetVsCostProgressForRole &&
      project?.is_tracking_enabled &&
      project?.budget_type !== "not_billable" &&
      project?.budget > 0
    );
  }, [project?.budget, project?.budget_type, project?.is_tracking_enabled, showBudgetVsCostProgressForRole]);

  const showProfitabilityProgress = useMemo(() => {
    return showBudgetVsCostProgressForRole && project?.is_tracking_enabled;
  }, [project?.is_tracking_enabled, showBudgetVsCostProgressForRole]);

  const showUninvoicedWidget = useMemo(
    () => showProfitabilityProgress && project?.budget_type !== "not_billable",
    [project?.budget_type, showProfitabilityProgress]
  );

  return {
    showTrackedVsEstimatedProgress,
    showBudgetVsCostProgress,
    showTrackingProgress,
    showTrackingProgressForRole,
    showProfitabilityProgress,
    showUninvoicedWidget,
  };
};
